import {
  AuditPortalRequestTemplate,
  AccountId,
  ApiDateTime,
  AuditPortalConnection,
  AuditPortalEngagementLink,
  AuditPortalRequest,
  ConnectInflo,
  EngagementAuditPortal,
  EngagementId,
  InfloEngagement,
  InfloTeamMember,
  LinkInfloEngagement,
  nominate,
  SendToInflo,
  TprmExternalQuestionnaireId,
} from "platformed-client/types";
import {platformedClient} from "..";
import {clearLocalState, readLocalState, writeLocalState} from "../../hooks/localState";
import {invalidateQueries, q} from "../../state";
import {Type} from "../../utils/typing";

export async function list(): Promise<AuditPortalConnection[]> {
  return platformedClient.internalRequest("get", "/region/{region}/api/tprm/audit_portal_connections", {});
}

export async function connectInflo(body: ConnectInflo): Promise<AuditPortalConnection> {
  const connection = await platformedClient.internalRequest(
    "put",
    "/region/{region}/api/tprm/audit_portal_connections/inflo",
    {body},
  );
  await invalidateQueries([q.tprm.auditPortalConnections.filter(), q.tprm.engagementAuditPortal.filter()]);
  await clearInfloEngagementsCache();
  return connection;
}

export async function setInfloRequestTemplate(template: AuditPortalRequestTemplate): Promise<void> {
  await platformedClient.internalRequest(
    "put",
    "/region/{region}/api/tprm/audit_portal_connections/inflo/request_template",
    {body: template},
  );
  await invalidateQueries([q.tprm.auditPortalConnections.filter(), q.tprm.engagementAuditPortal.filter()]);
}

export async function disconnectInflo(): Promise<void> {
  await platformedClient.internalRequest("delete", "/region/{region}/api/tprm/audit_portal_connections/inflo", {});
  await invalidateQueries([q.tprm.auditPortalConnections.filter(), q.tprm.engagementAuditPortal.filter()]);
  await clearInfloEngagementsCache();
}

export type CachedInfloEngagements = {fetched_at: ApiDateTime; engagements: InfloEngagement[]};

// The firm-wide list runs to thousands of engagements across several Inflo pages, so it is kept
// per account until the user refreshes it or the connection changes.
const INFLO_ENGAGEMENTS_CACHE = Type.Union([
  Type.Null(),
  Type.Unsafe<CachedInfloEngagements>(
    Type.Object({
      fetched_at: Type.String(),
      engagements: Type.Array(
        Type.Object({
          engagement_id: Type.Number(),
          company_name: Type.String(),
          engagement_type: Type.Union([Type.String(), Type.Null()]),
          year_start: Type.String(),
          year_end: Type.String(),
        }),
      ),
    }),
  ),
]);

const infloEngagementsCacheKey = (accountId: AccountId) => `tprm.infloEngagements.v1.${accountId}`;

export async function listInfloEngagements(accountId: AccountId): Promise<CachedInfloEngagements> {
  const cached = readLocalState(infloEngagementsCacheKey(accountId), INFLO_ENGAGEMENTS_CACHE, null);
  if (cached) {
    return cached;
  }
  const engagements: InfloEngagement[] = await platformedClient.internalRequest(
    "get",
    "/region/{region}/api/tprm/audit_portal_connections/inflo/engagements",
    {},
  );
  const fetched = {fetched_at: nominate("datetime", new Date().toISOString()), engagements};
  try {
    writeLocalState(infloEngagementsCacheKey(accountId), INFLO_ENGAGEMENTS_CACHE, fetched);
  } catch (e) {
    console.warn("Could not cache Inflo engagements", e);
  }
  return fetched;
}

export async function refreshInfloEngagements(accountId: AccountId): Promise<void> {
  clearLocalState(new RegExp(`^${infloEngagementsCacheKey(accountId).replaceAll(".", "\\.")}$`));
  await invalidateQueries([q.tprm.infloEngagements.filter(accountId)]);
}

async function clearInfloEngagementsCache() {
  clearLocalState(/^tprm\.infloEngagements\./);
  await invalidateQueries([q.tprm.infloEngagements.filter()]);
}

export async function getEngagementAuditPortal(engagementId: EngagementId): Promise<EngagementAuditPortal> {
  return platformedClient.internalRequest("get", "/region/{region}/api/tprm/engagements/{engagement_id}/audit_portal", {
    path: {engagement_id: engagementId},
  });
}

export async function linkInfloEngagement(
  engagementId: EngagementId,
  body: LinkInfloEngagement,
): Promise<AuditPortalEngagementLink> {
  const link = await platformedClient.internalRequest(
    "put",
    "/region/{region}/api/tprm/engagements/{engagement_id}/audit_portal/inflo",
    {path: {engagement_id: engagementId}, body},
  );
  await invalidateQueries([q.tprm.engagementAuditPortal.filter(engagementId), q.tprm.infloTeam.filter(engagementId)]);
  return link;
}

export async function unlinkEngagement(engagementId: EngagementId): Promise<void> {
  await platformedClient.internalRequest(
    "delete",
    "/region/{region}/api/tprm/engagements/{engagement_id}/audit_portal",
    {path: {engagement_id: engagementId}},
  );
  await invalidateQueries([q.tprm.engagementAuditPortal.filter(engagementId), q.tprm.infloTeam.filter(engagementId)]);
}

export async function listInfloTeam(engagementId: EngagementId): Promise<InfloTeamMember[]> {
  return platformedClient.internalRequest(
    "get",
    "/region/{region}/api/tprm/engagements/{engagement_id}/audit_portal/inflo/team",
    {path: {engagement_id: engagementId}},
  );
}

export async function listRequests(engagementId: EngagementId): Promise<AuditPortalRequest[]> {
  return platformedClient.internalRequest(
    "get",
    "/region/{region}/api/tprm/engagements/{engagement_id}/audit_portal/requests",
    {path: {engagement_id: engagementId}},
  );
}

export async function syncRequests(engagementId: EngagementId): Promise<AuditPortalRequest[]> {
  const requests = await platformedClient.internalRequest(
    "post",
    "/region/{region}/api/tprm/engagements/{engagement_id}/audit_portal/requests/sync",
    {path: {engagement_id: engagementId}},
  );
  await invalidateQueries([q.tprm.auditPortalRequests.filter(engagementId)]);
  return requests;
}

export async function sendToInflo(
  engagementId: EngagementId,
  externalQuestionnaireId: TprmExternalQuestionnaireId,
  body: SendToInflo,
): Promise<AuditPortalRequest> {
  const request = await platformedClient.internalRequest(
    "post",
    "/region/{region}/api/tprm/external_questionnaires/{external_questionnaire_id}/audit_portal/inflo",
    {path: {external_questionnaire_id: externalQuestionnaireId}, body},
  );
  await invalidateQueries([q.tprm.auditPortalRequests.filter(engagementId)]);
  return request;
}
